import { Button, TextArea } from "@clockwise/design-system";
import { Close } from "@clockwise/design-system/icons";
import { Psychology } from "@clockwise/icons";
import { LoadingSpinner } from "@clockwise/web-commons/src/ui/LoadingSpinner";
import { Dialog } from "@material-ui/core";
import { isEmpty } from "lodash";
import React, { KeyboardEvent, useEffect, useRef, useState } from "react";

const EMPTY_TEAM_TEXT = "The people on my team are ...";
const EMPTY_BOSS_TEXT = "My boss is ...";
const Input = ({
  onSubmit,
  inputRef,
  setInputEmpty,
}: {
  onSubmit: (text: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement> | null;
  setInputEmpty: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const anAddMemory = () => {
    if (inputRef?.current && !isEmpty(inputRef.current.value.trim())) {
      onSubmit(inputRef.current.value);
      resetInput();
    }
  };

  const resetInput = () => {
    if (inputRef?.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // if just enter, submit. If enter + shift allow new line
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      anAddMemory();
    }
  };

  const onChange = () => {
    if (inputRef?.current && isEmpty(inputRef?.current?.value.trim())) {
      setInputEmpty(true);
    } else {
      setInputEmpty(false);
    }
  };

  return (
    <div className="cw-py-1 cw-flex cw-justify-between cw-items-center">
      <TextArea
        inline
        borderless
        fullWidth
        placeholder="Add another"
        ref={inputRef}
        onKeyDown={onKeyDown}
        onChange={onChange}
        cw-id={"add-another-input"}
      />
    </div>
  );
};

const EmptyStateInput = ({
  _ref,
  placeholder,
  onChange,
}: {
  _ref: React.RefObject<HTMLTextAreaElement> | null;
  placeholder: string;
  onChange: () => void;
}) => {
  const ref = _ref;

  const clearInput = () => {
    if (ref && ref.current) {
      ref.current.value = "";
      onChange();
    }
  };

  useEffect(() => {
    // on mount, make intitial value the placeholder test
    // this still allows the user to edit and delete this text
    if (ref?.current) {
      ref.current.value = placeholder;
    }
  }, [ref]);

  const cwIdBase = placeholder.replace(/\s/g, "-");
  return (
    <div className="cw-py-1 cw-flex cw-justify-between cw-items-center">
      <TextArea
        inline
        borderless
        fullWidth
        ref={ref}
        onChange={onChange}
        cw-id={cwIdBase + "-input"}
      />
      <div className="cw-ml-1">
        <Button
          size="mini"
          variant="text"
          sentiment="positive"
          onClick={clearInput}
          cw-id={cwIdBase + "-clear"}
        >
          <Close className="cw-w-5 cw-h-5 cw-text-muted" />
        </Button>
      </div>
    </div>
  );
};

const EmptyState = ({
  teamRef,
  bossRef,
  setAreInputsEmpty,
}: {
  teamRef: React.RefObject<HTMLTextAreaElement> | null;
  bossRef: React.RefObject<HTMLTextAreaElement> | null;
  setAreInputsEmpty: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const onChange = () => {
    const teamValue = teamRef?.current?.value.trim();
    const bossValue = bossRef?.current?.value.trim();

    // if is empty or matches placer, consider empty
    if (
      (isEmpty(teamValue) || teamValue === EMPTY_TEAM_TEXT) &&
      (isEmpty(bossValue) || bossValue === EMPTY_BOSS_TEXT)
    ) {
      setAreInputsEmpty(true);
    } else {
      setAreInputsEmpty(false);
    }
  };

  return (
    <>
      <EmptyStateInput _ref={teamRef} placeholder={EMPTY_TEAM_TEXT} onChange={onChange} />
      <DashedBorder />
      <EmptyStateInput _ref={bossRef} placeholder={EMPTY_BOSS_TEXT} onChange={onChange} />
      <DashedBorder />
    </>
  );
};

const DashedBorder = () => {
  return <div className="cw-border-b cw-border-dashed cw-border-default cw-w-full" />;
};

export const AIMemoryModal = ({
  isOpen,
  memoryList,
  onAdd,
  onDelete,
  onSave,
  onClose,
  loading,
  disabled,
  fetchedMemoriesEmpty,
}: {
  isOpen: boolean;
  memoryList: string[];
  onAdd: (text: string) => void;
  onDelete: (index: number) => void;
  onSave: (inputs?: string[]) => void;
  onClose: () => void;
  loading: boolean;
  disabled: boolean;
  fetchedMemoriesEmpty: boolean;
}) => {
  const [emptyStateInputsEmpty, setEmptyStateInputsEmpty] = useState(true);
  const [addAnotherEmpty, setAddAnotherEmpty] = useState(true);
  const teamEmptyStateInput = useRef<HTMLTextAreaElement>(null);
  const bossEmptyStateInput = useRef<HTMLTextAreaElement>(null);
  const addAnotherInput = useRef<HTMLTextAreaElement>(null);

  const onSubmit = () => {
    const inputValues = [];

    if (addAnotherInput?.current?.value) {
      inputValues.push(addAnotherInput?.current?.value);
    }
    if (fetchedMemoriesEmpty) {
      // Only add team input values if they differ from the empty state/ placeholder text
      if (
        teamEmptyStateInput?.current?.value &&
        teamEmptyStateInput?.current?.value.trim() !== EMPTY_TEAM_TEXT
      ) {
        inputValues.push(teamEmptyStateInput.current.value);
      }
      // Only add bossinput values if they differ from the empty state/ placeholder text
      if (
        bossEmptyStateInput?.current?.value &&
        bossEmptyStateInput?.current?.value.trim() !== EMPTY_BOSS_TEXT
      ) {
        inputValues.push(bossEmptyStateInput.current.value);
      }
    }
    onSave(inputValues);
  };

  const isSaveDisabled = () => {
    if (fetchedMemoriesEmpty) {
      return emptyStateInputsEmpty && isEmpty(memoryList) && addAnotherEmpty;
    } else {
      return (disabled || loading) && addAnotherEmpty;
    }
  };

  return (
    <Dialog open={isOpen} maxWidth="sm">
      <div className="cw-flex-col">
        <div className="cw-body-lg cw-font-bold w-text-default cw-px-3 cw-py-3 cw-flex cw-items-center">
          <Psychology className="cw-text-busy cw-w-[22px] cw-h-[22px] cw-mr-1 cw-font-bold" />
          What should Clockwise remember when scheduling?
        </div>
        <div className="cw-border-b cw-border-solid cw-border-muted cw-w-full" />
        <div>
          {loading ? (
            <div className="cw-w-full cw-flex cw-items-center cw-justify-center cw-px-4 cw-h-[120px]">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="cw-px-3 cw-ml-1">
              {fetchedMemoriesEmpty && (
                <EmptyState
                  teamRef={teamEmptyStateInput}
                  bossRef={bossEmptyStateInput}
                  setAreInputsEmpty={setEmptyStateInputsEmpty}
                />
              )}
              {memoryList.map((memory, index) => (
                <div key={`${memory.slice(0, 10)}-${index}`} className="cw-flex-col">
                  <div className="cw-flex cw-justify-between cw-items-center">
                    <div className="cw-text-14 cw-py-3 cw-text-default cw-whitespace-pre-line">
                      {memory}
                    </div>
                    <Button
                      variant="text"
                      size="mini"
                      sentiment="neutral"
                      onClick={() => onDelete(index)}
                    >
                      <Close className="cw-w-5 cw-h-5 cw-text-muted" />
                    </Button>
                  </div>
                  <DashedBorder />
                </div>
              ))}

              <Input
                onSubmit={onAdd}
                inputRef={addAnotherInput}
                setInputEmpty={setAddAnotherEmpty}
              />
              <DashedBorder />
            </div>
          )}
        </div>

        <div className="cw-px-4 cw-py-3">
          <div className="cw-caption cw-text-subtle cw-rounded-lg cw-bg-neutral-inset cw-py-2 cw-px-4 cw-inline-block">
            Suggestions: “When I say my boss, I mean Anna” or “The Design Team is Ted, Angie, Ross”
          </div>
        </div>
      </div>
      <div className="cw-py-2 cw-px-3 cw-border-t cw-border-solid cw-border-muted cw-justify-end cw-flex ">
        <div className="cw-mx-1">
          <Button
            size="small"
            variant="outlined"
            sentiment="neutral"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
        <div className="cw-mx-1">
          <Button
            cw-id="save-button-memory-modal"
            size="small"
            sentiment="positive"
            onClick={onSubmit}
            disabled={isSaveDisabled()}
          >
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
